class ShareToStory extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.wp-share-story');
  }

  connectedCallback() {
    if (!this.button || !this.canShareFiles()) return;

    this.button.hidden = false;
    this.button.addEventListener('click', this.onClick.bind(this));
  }

  canShareFiles() {
    if (!('share' in navigator) || !('canShare' in navigator)) return false;

    try {
      const probe = new File([''], 'probe.png', { type: 'image/png' });
      return navigator.canShare({ files: [probe] });
    } catch (error) {
      return false;
    }
  }

  async onClick() {
    if (this.button.disabled) return;
    this.button.disabled = true;

    try {
      const file = await this.buildStoryImage();
      await navigator.share({
        files: [file],
        title: this.dataset.productTitle || '',
      });
    } catch (error) {
      if (error && error.name !== 'AbortError') {
        console.error('Share to Story failed', error);
      }
    } finally {
      this.button.disabled = false;
    }
  }

  async buildStoryImage() {
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1920;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#0a0f1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    let cursorY = 140;

    if (this.dataset.productImage) {
      const image = await this.loadImage(this.dataset.productImage);
      cursorY = this.drawProductImage(ctx, image, cursorY);
    }

    cursorY = this.drawHeading(ctx, cursorY + 80);
    this.drawSizeTable(ctx, cursorY + 60);

    return this.canvasToFile(canvas);
  }

  loadImage(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Could not load product image'));
      image.src = src;
    });
  }

  drawProductImage(ctx, image, y) {
    const targetWidth = 880;
    const targetHeight = targetWidth * (image.height / image.width);
    const x = (1080 - targetWidth) / 2;
    ctx.drawImage(image, x, y, targetWidth, targetHeight);
    return y + targetHeight;
  }

  drawHeading(ctx, y) {
    ctx.textAlign = 'center';
    ctx.fillStyle = '#eef0f3';

    ctx.font = '600 56px "IBM Plex Mono", monospace';
    y = this.wrapText(ctx, this.dataset.productTitle || '', 540, y, 940, 64);

    ctx.font = '500 46px "IBM Plex Mono", monospace';
    ctx.fillText(this.dataset.productPrice || '', 540, (y += 74));

    if (this.dataset.moq) {
      ctx.font = '400 32px "IBM Plex Mono", monospace';
      ctx.fillStyle = 'rgba(238, 240, 243, 0.68)';
      ctx.fillText(this.dataset.moq, 540, (y += 56));
    }

    return y;
  }

  wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    const words = text.split(' ');
    let line = '';
    let lineY = y;

    words.forEach((word, index) => {
      const testLine = line ? `${line} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth && line) {
        ctx.fillText(line, x, lineY);
        line = word;
        lineY += lineHeight;
      } else {
        line = testLine;
      }
      if (index === words.length - 1) ctx.fillText(line, x, lineY);
    });

    return lineY;
  }

  drawSizeTable(ctx, y) {
    const rows = this.getSizeRows();
    if (!rows.length) return;

    ctx.fillStyle = 'rgba(238, 240, 243, 0.5)';
    ctx.font = '600 26px "IBM Plex Mono", monospace';
    ctx.textAlign = 'left';
    ctx.fillText('SIZE', 140, y);
    ctx.textAlign = 'right';
    ctx.fillText('AVAILABLE', 940, y);
    y += 20;

    ctx.strokeStyle = 'rgba(238, 240, 243, 0.24)';
    ctx.beginPath();
    ctx.moveTo(140, y);
    ctx.lineTo(940, y);
    ctx.stroke();
    y += 50;

    ctx.font = '600 34px "IBM Plex Mono", monospace';
    ctx.fillStyle = '#eef0f3';

    rows.forEach((row) => {
      ctx.textAlign = 'left';
      ctx.fillText(row.size, 140, y);
      ctx.textAlign = 'right';
      ctx.fillText(row.available, 940, y);
      y += 56;
    });
  }

  getSizeRows() {
    const selector = this.dataset.tableSelector || '.wp-table';
    const table = document.querySelector(selector);
    if (!table) return [];

    return Array.from(table.querySelectorAll('tbody tr[data-unit-price]'))
      .map((row) => {
        const size = row.querySelector('.wp-row__size')?.textContent.trim() || '';
        const available = row.querySelector('.wp-row__available')?.textContent.trim() || '';
        return { size, available };
      })
      .filter((row) => row.size);
  }

  canvasToFile(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas produced no image data'));
          return;
        }
        resolve(new File([blob], 'share-to-story.png', { type: 'image/png' }));
      }, 'image/png');
    });
  }
}

customElements.define('share-to-story', ShareToStory);
